import { useSession } from '@/lib/session';
import { hasPermission, type PermissionName } from '@/lib/permissions';
import Icon from './Icon';

/**
 * PermissionRequiredBanner — drop into any page whose features
 * need a named permission. Renders an amber "access required"
 * banner if the current session doesn't hold `permission`;
 * renders nothing if it does. Pattern mirrored from the design
 * reference's Exports page banner.
 *
 * Usage:
 *
 *   <PermissionRequiredBanner permission="reorder.draft.edit" />
 *   <PermissionRequiredBanner
 *     permission="reorder.draft.edit"
 *     message="Approving and editing reorder quantities is available to managers and owners."
 *   />
 *
 * The page content below the banner still renders — by design,
 * the banner explains the restriction without hiding what the
 * feature looks like. URL-typed access by an underprivileged
 * user lands on the same page; no redirect.
 *
 * 3D.1 — supersedes RoleRequiredBanner. The prop is now the
 * named permission rather than a target role; the actual
 * role/grant logic lives in has_permission() and
 * defaultPermissionsForRole(). The default body copy is
 * generic — pages that want feature-specific framing pass a
 * `message` override (ReorderPage does this).
 */

const DEFAULT_TITLE = 'Additional access required';

const DEFAULT_MESSAGE =
  'This feature requires additional permissions. ' +
  'Ask your clinic admin if you need access.';

interface PermissionRequiredBannerProps {
  permission: PermissionName;
  /** Override the default body copy — useful when the page wants
   *  feature-specific framing (e.g. "Approving and editing reorder
   *  quantities..."). */
  message?: string;
}

export default function PermissionRequiredBanner({
  permission,
  message,
}: PermissionRequiredBannerProps) {
  const session = useSession();
  if (hasPermission(session, permission)) return null;

  return (
    <div className="mx-4 mt-4 mb-2 p-3 rounded-xl bg-amber-50 border border-amber-100 flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
        <Icon name="shield" className="w-4 h-4 text-amber-700" strokeWidth={2} />
      </div>
      <div className="text-[12px] text-amber-900 leading-snug">
        <div className="font-semibold">{DEFAULT_TITLE}</div>
        <div className="text-amber-800/80 mt-0.5">
          {message ?? DEFAULT_MESSAGE}
        </div>
      </div>
    </div>
  );
}
